// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@awc/App/Components/Notify";
import checkGuestCheckoutEmail from "@awc/App/Handlers/SignIn/CheckGuestCheckoutEmail";
import TextField, { TextFieldProps } from "@awc/MobiusOverrides/TextField";
import { xValidateOnSubmit } from "@awc/XDL/Utilities";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import { getLocation, getReturnUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SignInResetPasswordForm from "@insite/content-library/Widgets/SignIn/SignInResetPasswordForm";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconProps } from "@insite/mobius/Icon/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    title = "title",
    fullWidthButton = "fullWidthButton",
}

const mapStateToProps = (state: ApplicationState) => {
    const accountSettings = getSettingsCollection(state).accountSettings;
    const { allowGuestCheckout } = accountSettings;
    let returnUrl = getReturnUrl(state);
    if (!returnUrl && state.components.signInOverlay.isOpen) {
        returnUrl = getLocation(state).pathname;
    }
    const shippingPageUrl = getPageLinkByPageType(state, "CheckoutShippingPage")?.url;
    const referredFromShipping = returnUrl === shippingPageUrl;
    const onePageCheckoutPageUrl = getPageLinkByPageType(state, "OnePageCheckoutPage")?.url;
    const referredFromOnePageCheckout = returnUrl === onePageCheckoutPageUrl;

    return {
        accountSettings,
        allowGuestCheckout:
            allowGuestCheckout &&
            !getCurrentUserIsGuest(state) &&
            (referredFromShipping || referredFromOnePageCheckout),
        returnUrl,
    };
};

const mapDispatchToProps = {
    checkGuestCheckoutEmail,
    signInAsGuest,
    signIn,
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.fullWidthButton]: boolean;
    };
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface SignInGuestStyles {
    container?: GridContainerProps;
    titleGridItem?: GridItemProps;
    emailGridItem?: GridItemProps;
    buttonGridItem?: GridItemProps;
    title?: TypographyProps;
    emailTextField?: TextFieldProps;
    continueButton?: ButtonPresentationProps;
    centeringWrapper?: InjectableCss;
    form?: InjectableCss;
    accountExistsModal?: ModalPresentationProps;
    modalContent?: InjectableCss;
    modalMessage?: TypographyProps;
    modalEmailTextField?: TextFieldProps;
    modalPasswordTextField?: TextFieldProps;
    modalPasswordIcon?: IconProps;
    forgotPasswordLink?: LinkPresentationProps;
    rememberMeForgotPasswordWrapper?: InjectableCss;
    rememberMeCheckboxGroup?: CheckboxGroupProps;
    rememberMeCheckbox?: CheckboxProps;
    resetPasswordModal?: ModalPresentationProps;
    modalActions?: InjectableCss;
    modalSignInButton?: ButtonPresentationProps;
    modalContinueAsGuestButton?: ButtonPresentationProps;
    modalForm?: InjectableCss;
}

export const signInGuestStyles: SignInGuestStyles = {};

const styles = signInGuestStyles;
const StyledForm = getStyledWrapper("form");
const signInGuestFormId = "signInGuestForm";
const signInGuestModalFormId = "signInGuestModalForm";

const SignInGuest: FC<Props> = ({
    fields,
    accountSettings,
    allowGuestCheckout,
    returnUrl,
    checkGuestCheckoutEmail,
    signInAsGuest,
    signIn,
}) => {
    const { isEditing } = useShellContext();
    const [email, setEmail] = useState("");
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [emailError, setEmailError] = useState<React.ReactNode>("");
    const [isCheckingEmail, setIsCheckingEmail] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);
    const [isAccountExistsModalOpen, setIsAccountExistsModalOpen] = useState(false);
    const [signInEmail, setSignInEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [isSignInClicked, setIsSignInClicked] = useState(false);
    const [signInErrorMessage, setSignInErrorMessage] = useState("");
    const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);

    const showRememberMe = accountSettings.rememberMe;

    if (!allowGuestCheckout && !isEditing) {
        return null;
    }

    const validateGuestEmail = (value: string) => {
        if (!value) {
            return siteMessage("SignInInfo_Email_Required");
        }
        if (!validateEmail(value)) {
            return siteMessage("SignInInfo_Email_Validation");
        }
        return "";
    };

    const validateSignInUserName = (value: string) => {
        if (!value) {
            return accountSettings.useEmailAsUserName
                ? siteMessage("SignInInfo_Email_Required")
                : siteMessage("SignInInfo_UserName_Required");
        }
        if (accountSettings.useEmailAsUserName && !validateEmail(value)) {
            return siteMessage("SignInInfo_Email_Validation");
        }
        return "";
    };

    const handleContinueAsGuest = () => {
        if (!returnUrl) {
            return;
        }
        setShowSpinner(true);
        setIsAccountExistsModalOpen(false);
        signInAsGuest({ returnUrl });
    };

    const handleCheckEmail = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsSubmitted(true);

        const validationError = validateGuestEmail(email);
        setEmailError(validationError);
        if (validationError) {
            return;
        }

        if (!returnUrl) {
            return;
        }

        setIsCheckingEmail(true);
        checkGuestCheckoutEmail({
            email,
            onSuccess: result => {
                setIsCheckingEmail(false);
                if (!result.successful || !result.result) {
                    return;
                }

                if (result.result.emailExists) {
                    setSignInEmail("");
                    setPassword("");
                    setRememberMe(false);
                    setIsSignInClicked(false);
                    setSignInErrorMessage("");
                    setIsAccountExistsModalOpen(true);
                    return;
                }

                handleContinueAsGuest();
            },
            onError: errorMessage => {
                setIsCheckingEmail(false);
                setEmailError(errorMessage);
            },
        });
    };

    const handleSignIn = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsSignInClicked(true);

        if (xValidateOnSubmit(signInGuestModalFormId).length > 0) {
            return;
        }

        if (validateSignInUserName(signInEmail) || !password) {
            return;
        }

        setSignInErrorMessage("");
        signIn({
            userName: signInEmail,
            password,
            rememberMe,
            returnUrl,
            onError: error => {
                setSignInErrorMessage(error);
            },
        });
    };

    const onResetPasswordClose = () => {
        setIsResetPasswordModalOpen(false);
    };

    const signInEmailErrorMessage = isSignInClicked ? validateSignInUserName(signInEmail) : "";
    const passwordErrorMessage =
        !password && isSignInClicked ? siteMessage("SignInInfo_Password_Required") : signInErrorMessage || "";

    if (showSpinner) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    return (
        <>
            <GridContainer {...styles.container}>
                <GridItem {...styles.titleGridItem}>
                    <Typography {...styles.title}>{fields.title || translate("Checkout as Guest")}</Typography>
                </GridItem>
                <GridItem {...styles.emailGridItem}>
                    <StyledForm {...styles.form} id={signInGuestFormId} onSubmit={handleCheckEmail} noValidate>
                        <TextField
                            id="signInGuestEmail"
                            {...styles.emailTextField}
                            type="email"
                            label={translate("Email")}
                            placeholder={translate("Email")}
                            value={email}
                            autoComplete="email"
                            data-test-selector="signInGuest_email"
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const value = e.currentTarget.value;
                                setEmail(value);
                                if (isSubmitted) {
                                    setEmailError(validateGuestEmail(value));
                                }
                            }}
                            error={isSubmitted ? emailError : ""}
                        />
                    </StyledForm>
                </GridItem>
                <GridItem {...styles.buttonGridItem}>
                    <Button
                        {...styles.continueButton}
                        type="submit"
                        form={signInGuestFormId}
                        disabled={isCheckingEmail}
                        data-test-selector="signInGuest_continue"
                        aria-label={translate("Continue as Guest")}
                        {...{ isFullWidth: fields.fullWidthButton }}
                    >
                        {translate("Continue as Guest")}
                    </Button>
                </GridItem>
            </GridContainer>
            <Modal
                headline={translate("Sign In")}
                {...styles.accountExistsModal}
                isOpen={isAccountExistsModalOpen}
                handleClose={() => setIsAccountExistsModalOpen(false)}
                data-test-selector="signInGuest_accountExistsModal"
            >
                <StyledWrapper {...styles.modalContent}>
                    <Notify
                        type="warning"
                        {...styles.modalMessage}
                        data-test-selector="signInGuest_accountExistsMessage"
                    >
                        {siteMessage("Custom_GuestCheckout_EmailExists")}
                    </Notify>
                    <StyledForm {...styles.modalForm} id={signInGuestModalFormId} onSubmit={handleSignIn} noValidate>
                        <TextField
                            id="signInGuestModalEmail"
                            {...styles.modalEmailTextField}
                            label={accountSettings.useEmailAsUserName ? translate("Email") : translate("Username")}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSignInEmail(e.currentTarget.value)}
                            value={signInEmail}
                            error={signInEmailErrorMessage}
                            autoComplete="username"
                            data-test-selector="signInGuest_modalEmail"
                            placeholder={
                                accountSettings.useEmailAsUserName ? translate("Email") : translate("Username")
                            }
                            type={accountSettings.useEmailAsUserName ? "email" : "userName"}
                        />
                        <TextField
                            id="signInGuestModalPassword"
                            {...styles.modalPasswordTextField}
                            label={translate("Password")}
                            placeholder={translate("Password")}
                            value={password}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.currentTarget.value)}
                            error={passwordErrorMessage}
                            iconProps={{
                                ...styles.modalPasswordIcon,
                                src: showPassword ? EyeOff : Eye,
                            }}
                            iconClickableProps={{
                                onClick: () => setShowPassword(!showPassword),
                                type: "button",
                                "aria-label": translate("Show Password"),
                            }}
                            clickableText={password}
                            autoComplete="current-password"
                            data-test-selector="signInGuest_modalPassword"
                            type={showPassword ? "text" : "password"}
                            validateAs="password"
                            {...{ formId: signInGuestModalFormId }}
                        />
                        <StyledWrapper {...styles.rememberMeForgotPasswordWrapper}>
                            {showRememberMe && (
                                <CheckboxGroup {...styles.rememberMeCheckboxGroup}>
                                    <Checkbox
                                        uid="signInGuestRememberMe"
                                        {...styles.rememberMeCheckbox}
                                        checked={rememberMe}
                                        onChange={(_, value) => setRememberMe(value)}
                                    >
                                        {translate("Remember Me")}
                                    </Checkbox>
                                </CheckboxGroup>
                            )}
                            <Link
                                {...styles.forgotPasswordLink}
                                type="button"
                                onClick={() => setIsResetPasswordModalOpen(true)}
                                data-test-selector="signInGuest_forgotPassword"
                                aria-label={translate("Forgot Password")}
                            >
                                {translate("Forgot Password?")}
                            </Link>
                        </StyledWrapper>
                        <StyledWrapper {...styles.modalActions}>
                            <Button
                                {...styles.modalContinueAsGuestButton}
                                type="button"
                                onClick={handleContinueAsGuest}
                                data-test-selector="signInGuest_modalContinueAsGuest"
                                aria-label={translate("Continue As Guest")}
                            >
                                {translate("Continue As Guest")}
                            </Button>
                            <Button
                                {...styles.modalSignInButton}
                                type="submit"
                                data-test-selector="signInGuest_modalSignIn"
                                aria-label={translate("Sign In")}
                            >
                                {translate("Sign In")}
                            </Button>
                        </StyledWrapper>
                    </StyledForm>
                </StyledWrapper>
            </Modal>
            <Modal
                headline={translate("Reset Password")}
                {...styles.resetPasswordModal}
                isOpen={isResetPasswordModalOpen}
                handleClose={onResetPasswordClose}
                data-test-selector="signInGuest_resetPasswordModal"
            >
                <SignInResetPasswordForm onClose={onResetPasswordClose} />
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(SignInGuest),
    definition: {
        displayName: "Sign In Guest",
        allowedContexts: ["SignInPage", "SignInOverlay"],
        group: "Sign In",
        icon: "User",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Checkout as Guest",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.fullWidthButton,
                displayName: "Full Width Button",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
